import { ThunkAction } from "redux-thunk";

import {
	EventQuery,
	fetchEventGroupRequest,
	fetchEventsRequest,
	groupQueries,
	isSubQueryOf
} from "lib/build/events";

import { EventsState, EventsStateItem } from "src/features/events/types";
import {
	createGenericError,
	NotificationsAction
} from "src/features/notifications";

export type EventsAction =
	| FetchEventsStart
	| FetchEventsSuccess
	| FetchEventsFailure
	| RefetchEventsStart
	| RefetchEventsSuccess
	| RefetchEventsFailure;

interface FetchEventsStart {
	type: "FETCH_EVENTS_START";
	payload: { queries: EventQuery[] };
}

interface FetchEventsSuccess {
	type: "FETCH_EVENTS_SUCCESS";
	payload: { items: EventsStateItem[] };
}

interface FetchEventsFailure {
	type: "FETCH_EVENTS_FAILURE";
	payload: { error: unknown };
}

interface RefetchEventsStart {
	type: "REFETCH_EVENTS_START";
	payload: { query: EventQuery };
}

interface RefetchEventsSuccess {
	type: "REFETCH_EVENTS_SUCCESS";
	payload: EventsStateItem;
}

interface RefetchEventsFailure {
	type: "REFETCH_EVENTS_FAILURE";
	payload: { error: unknown };
}

interface FetchEventsOptions {
	org: string;
	mod: string;
	patientId: string;
	queries: EventQuery[];
}

export function fetchEvents({
	org,
	mod,
	patientId,
	queries
}: FetchEventsOptions): ThunkAction<
	Promise<void>,
	unknown,
	void,
	EventsAction | NotificationsAction
> {
	return async dispatch => {
		dispatch({ type: "FETCH_EVENTS_START", payload: { queries } });
		try {
			const groups = groupQueries(queries);
			const requests = groups.map(queries =>
				fetchEventGroupRequest({ org, mod, patientId, queries })
			);
			const items = (await Promise.all(requests)).flat();
			dispatch({ type: "FETCH_EVENTS_SUCCESS", payload: { items } });
		} catch (error) {
			dispatch({ type: "FETCH_EVENTS_FAILURE", payload: { error } });
			dispatch(createGenericError());
		}
	};
}

interface RefetchEventsOptions {
	org: string;
	mod: string;
	patientId: string;
	query: EventQuery;
}

export function refetchEvents({
	org,
	mod,
	patientId,
	query
}: RefetchEventsOptions): ThunkAction<
	Promise<void>,
	unknown,
	void,
	EventsAction | NotificationsAction
> {
	return async (dispatch, getState) => {
		const {
			events: { items }
		} = getState() as { events: EventsState };
		const queries = Object.values(items)
			.filter(item => isSubQueryOf(item.query, query))
			.map(item => item.query);
		if (queries.length === 0) {
			return;
		}
		for (const query of queries) {
			dispatch({ type: "REFETCH_EVENTS_START", payload: { query } });
			try {
				const events = await fetchEventsRequest({
					org,
					mod,
					patientId,
					query
				});
				dispatch({
					type: "REFETCH_EVENTS_SUCCESS",
					payload: { query, events }
				});
			} catch (error) {
				dispatch({
					type: "REFETCH_EVENTS_FAILURE",
					payload: { error }
				});
				dispatch(createGenericError());
			}
		}
	};
}
